import React, { useRef } from 'react';
import { Line } from 'react-chartjs-2';
import '../styles/NPSChart.css';

/**
 * NPSChart - Responsible only for rendering the chart and handling click interactions
 * 
 * This component has been simplified to focus on:
 * 1. Rendering the Line chart with NPS data
 * 2. Handling click events on data points
 * 3. Managing chart styling and background zones
 */
const NPSChart = ({
    chartData,
    chartConfig,
    showConfidenceIntervals,
    stockAxis = false,
    overlayKind = null, // 'price' | 'premium' | null (overrides stockAxis)
    onPointClick,
    // Axis titles are overridable so this chart can be reused for parallel
    // 0-100 metrics (e.g. LLM Recommendations) without forking the component.
    yAxisTitle = 'Net Promoter Score (Bayesian Estimate)',
    xAxisTitle = 'Time Period'
}) => {
    const chartRef = useRef(null);
    // Which secondary (y1) overlay axis to show, if any.
    const overlay = overlayKind || (stockAxis ? 'price' : null);
    
    // Prepare default chart data if none exists
    const displayChartData = chartData || {
        labels: ['No Data'],
        datasets: [{
            label: 'No Chart Data Available',
            data: [0],
            borderColor: 'gray',
            backgroundColor: 'lightgray',
        }]
    };

    // Make all points smaller and solid
    if (displayChartData && displayChartData.datasets) {
        displayChartData.datasets.forEach(dataset => {
            // Skip confidence bands and the stock-price overlay (keep it dot-free)
            if (!dataset.label?.includes('Confidence Band') && !dataset.isStock) {
                dataset.pointRadius = 3; // Smaller points
                dataset.pointHoverRadius = 5; // Slightly larger on hover
                dataset.pointBackgroundColor = dataset.borderColor; // Same as line color
                dataset.pointStyle = 'circle'; // Ensure circular points
            }
        });
    }

    // Prepare default config with click handling
    const displayConfig = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'nearest',
            intersect: true
        },
        onClick: (event) => {
            const chart = chartRef.current;
            if (!chart) {
                console.log('Chart ref not available');
                return;
            }

            const elements = chart.getElementsAtEventForMode(
                event.native, 
                'nearest', 
                { intersect: true }, 
                true
            );

            if (elements.length > 0) {
                const element = elements[0];
                const datasetIndex = element.datasetIndex;
                const index = element.index;
                const pointData = displayChartData.datasets[datasetIndex].data[index];
                const value = pointData.y || pointData;
                const label = displayChartData.labels[index];
                
                // Call the onPointClick prop with the point data if it exists
                if (onPointClick) {
                    onPointClick({
                        value,
                        label,
                        datasetIndex,
                        index,
                        pointData
                    });
                }
            } else {
                console.log('Clicked outside data points.');
            }
        },
        // Disable all animations
        animation: false,
        animations: {
            colors: false,
            x: false,
            y: false
        },
        transitions: {
            active: {
                animation: {
                    duration: 0
                }
            }
        },
        scales: {
            y: {
                min: 0,
                max: 100,
                title: {
                    display: true,
                    text: yAxisTitle,
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                }
            },
            x: {
                title: {
                    display: true,
                    text: xAxisTitle,
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                }
            },
            ...(overlay ? {
                y1: {
                    position: 'right',
                    grid: {
                        drawOnChartArea: false // don't clutter the NPS zones
                    },
                    title: {
                        display: true,
                        text: overlay === 'premium' ? 'Premium (%)' : 'Stock Price ($)',
                        font: {
                            size: 14,
                            weight: 'bold'
                        }
                    },
                    ticks: {
                        callback: (value) => (overlay === 'premium'
                            ? `${Number(value).toFixed(0)}%`
                            : `$${Number(value).toFixed(0)}`)
                    }
                }
            } : {})
        },
        plugins: {
            title: {
                display: false
            },
            legend: {
                display: true,
                position: 'top',
                align: 'end',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 15,
                    boxWidth: 10,
                    boxHeight: 10,
                    filter: function(legendItem) {
                        return !legendItem.text.includes('Confidence Band');
                    }
                },
                onClick: function(e, legendItem, legend) {
                    const index = legendItem.datasetIndex;
                    const chart = legend.chart;
                    const meta = chart.getDatasetMeta(index);

                    meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
                    
                    const mainLabel = chart.data.datasets[index].label;
                    chart.data.datasets.forEach((dataset, i) => {
                        if (dataset.label.includes('Confidence Band') && 
                            dataset.label.includes(mainLabel)) {
                            const confidenceMeta = chart.getDatasetMeta(i);
                            confidenceMeta.hidden = meta.hidden;
                        }
                    });

                    chart.update();
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const datasetLabel = context.dataset.label || '';
                        if (datasetLabel.includes('Confidence Band')) {
                            return null;
                        }
                        if (context.dataset.isStock) {
                            const v = context.parsed.y;
                            if (v == null) return null;
                            return overlay === 'premium'
                                ? `${datasetLabel}: ${v >= 0 ? '+' : ''}${Number(v).toFixed(1)}%`
                                : `${datasetLabel}: $${Number(v).toFixed(2)}`;
                        }
                        const value = context.parsed.y;
                        const dataPoint = context.dataset.data[context.dataIndex];
                        const confidenceBounds = dataPoint?.confidenceBounds;
                        const labels = [`${datasetLabel}: ${value.toFixed(1)}`];

                        if (confidenceBounds) {
                            labels.push(`95% Credible Interval: [${confidenceBounds.lower.toFixed(1)}, ${confidenceBounds.upper.toFixed(1)}]`);
                        }

                        // Sample size behind the point. Prefer the windowed count
                        // (responses pooled into a smoothed point) when present,
                        // else the per-bucket count. Shown independently of the
                        // credible interval so it always appears when there's data.
                        const sampleSize = dataPoint?.windowCount ?? dataPoint?.count;
                        if (sampleSize > 0) {
                            labels.push(`Sample Size: ${sampleSize}`);
                        }

                        return labels;
                    }
                }
            }
        },
        ...chartConfig
    };

    return (
        <div className="chart-wrapper" >
            <Line 
                ref={chartRef}
                data={displayChartData} 
                options={displayConfig}
                plugins={[{
                    id: 'backgroundColorPlugin',
                    beforeDraw: (chart) => {
                        const ctx = chart.canvas.getContext('2d');
                        const chartArea = chart.chartArea;
                        const yAxis = chart.scales.y;
                        const min = 0;
                        const low = 50;
                        const mid = 70;
                        const high = 100;

                        // Red zone (0-50)
                        ctx.fillStyle = 'rgba(255, 200, 200, 0.5)';
                        ctx.fillRect(
                            chartArea.left,
                            yAxis.getPixelForValue(low),
                            chartArea.right - chartArea.left,
                            yAxis.getPixelForValue(min) - yAxis.getPixelForValue(low)
                        );

                        // Yellow zone (50-70)
                        ctx.fillStyle = 'rgba(255, 255, 200, 0.5)';
                        ctx.fillRect(
                            chartArea.left,
                            yAxis.getPixelForValue(mid),
                            chartArea.right - chartArea.left,
                            yAxis.getPixelForValue(low) - yAxis.getPixelForValue(mid)
                        );

                        // Green zone (70-100)
                        ctx.fillStyle = 'rgba(200, 255, 200, 0.5)';
                        ctx.fillRect(
                            chartArea.left,
                            yAxis.getPixelForValue(high),
                            chartArea.right - chartArea.left,
                            yAxis.getPixelForValue(mid) - yAxis.getPixelForValue(high)
                        );
                    }
                }]}
            />
        </div>
    );
};

export default NPSChart;
